import { Inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { AppTheme } from './theme-switch.interface';
import { IconService } from '@tablesready/frontend-icons';
import { STORAGE_TOKEN } from '@tablesready/frontend-storage';

const STORAGE_KEY = 'app-theme';

@Injectable({ providedIn: 'root' })
export class ThemeSwitchService {
  themeChange$: Observable<AppTheme>;

  private readonly themeChangeSource$ = new Subject<AppTheme>();

  constructor(
    private readonly iconService: IconService,
    @Inject(STORAGE_TOKEN) private readonly storage: Storage,
  ) {
    this.themeChange$ = this.themeChangeSource$.asObservable();
  }

  private static getRoot(): HTMLElement | null {
    return document.getElementsByTagName('html').item(0);
  }

  private static removeThemeClasses(rootElement: HTMLElement): void {
    rootElement.classList.forEach((className) => {
      if (className?.includes('-theme')) {
        rootElement.classList.remove(className);
      }
    });
  }

  private static updateColorSchemeMeta(theme: AppTheme): void {
    const content = theme === AppTheme.dark ? 'dark' : 'light';
    const metaTags = document.getElementsByTagName('meta');

    for (let i = 0; i < metaTags.length; i++) {
      const tagEl = metaTags.item(i);

      if (tagEl?.name === 'color-scheme') {
        tagEl.setAttribute('content', content);
      }
    }
  }

  /**
   * @param persist when false the palette is applied but the user's saved
   *   preference is left alone — used to force light on unauthenticated screens
   *   without clobbering the theme they chose for the authenticated app.
   */
  setTheme(theme: AppTheme, persist = true): void {
    if (this.themeChangeSource$) {
      this.themeChangeSource$.next(theme);
    }

    const rootElement = ThemeSwitchService.getRoot();
    if (!rootElement) {
      throw new Error('no root element found to detemine theme');
    }

    if (persist) {
      this.storage.setItem(STORAGE_KEY, theme);
    }

    ThemeSwitchService.removeThemeClasses(rootElement);
    ThemeSwitchService.updateColorSchemeMeta(theme);

    rootElement.classList.add(theme);

    // Toggle rather than add: removeThemeClasses only strips `*-theme` classes,
    // so a bare `dark` left over from a previous dark session would otherwise
    // survive a switch to light.
    const isDark = theme === AppTheme.dark;
    rootElement.classList.toggle('dark', isDark);
    document.body.classList.toggle('dark', isDark);

    if (isDark) {
      this.iconService.setIconsLight();
    } else {
      this.iconService.setIconsDark();
    }
  }

  initializeTheme(): void {
    this.setTheme(this.getSavedTheme());
  }

  /**
   * The unauthenticated screens (login, signup, invite, verify, restore
   * password) are designed light-only, so the dark palette must not bleed into
   * them. Applied without persisting, so returning to the authenticated app
   * restores whatever theme the user actually picked.
   */
  forceLightTheme(): void {
    this.setTheme(AppTheme.light, false);
  }

  getSavedTheme(): AppTheme {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    let theme = this.storage.getItem(STORAGE_KEY) as AppTheme;

    if (!theme) {
      theme = prefersDark.matches ? AppTheme.dark : AppTheme.light;
      this.setTheme(theme);
      return theme;
    }

    return theme;
  }
}
